export function handleFormErrors(error: any, setErrors: (errors) => void) {
  const errors = serverFieldErrors(error);
  if (errors) {
    setErrors(errors);
  }
}

/**
 * The per-field messages of a FastAPI 422 (`detail: [{loc: ["body", field], msg}]`),
 * as `{field: msg}`; null when the failure is anything else (network, 5xx, a
 * string detail, or a 422 that names no body field). A caller that shows these
 * inline sends its request with `inlineValidationErrors: true` so the global
 * interceptor (src/api.ts) does not also toast each bare msg.
 */
export function serverFieldErrors(error: any): Record<string, string> | null {
  const detail = error?.response?.data?.detail;
  if (error?.response?.status !== 422 || !Array.isArray(detail)) return null;
  const errors: Record<string, string> = {};
  for (const e of detail) {
    const loc = Array.isArray(e?.loc) ? e.loc : [];
    const field = loc[0] === "body" ? loc[1] : undefined;
    if (typeof field === "string" && field !== "__root__" && !(field in errors)) {
      errors[field] = String(e?.msg ?? "Invalid");
    }
  }
  return Object.keys(errors).length ? errors : null;
}

/** A save's 422 field list together with the values that were posted. */
export interface SavedFieldErrors {
  errors: Record<string, string>;
  values: Record<string, any>;
}

/**
 * The entries of a save's 422 that still stand: a field the user has edited
 * since that save drops out of the summary (its message described a value
 * that is no longer there). `null` when none stand.
 */
export function standingFieldErrors(
  saved: SavedFieldErrors | null,
  values: Record<string, any> | null | undefined
): Record<string, string> | null {
  if (!saved) return null;
  const out: Record<string, string> = {};
  for (const [field, msg] of Object.entries(saved.errors)) {
    if (Object.is(values?.[field], saved.values?.[field])) out[field] = msg;
  }
  return Object.keys(out).length ? out : null;
}

export function handleAPIErrors(error: any, setErrors: (errors) => void) {
  if (
    error.response &&
    error.response.data &&
    Object.prototype.hasOwnProperty.call(error.response.data, "detail") &&
    error.response.data.detail
  ) {
    handleFormErrors(error, setErrors);
  }
}
